import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ExcelService } from 'src/app/services/excel.service';
import { LotteryService, LotteryDrillDownRow, LotterySubsidyPattern } from 'src/app/services/lottery.service';
import { LotteryDrillDownContext } from '../lottery-dashboard/lottery-dashboard.component';
import { SubsidyPatternViewComponent } from '../subsidy-pattern-view/subsidy-pattern-view.component';

@Component({
  selector: 'app-lottery-village',
  templateUrl: './lottery-village.component.html',
  styleUrls: ['./lottery-village.component.scss']
})
export class LotteryVillageComponent implements OnInit {

  @Input() drillData!: LotteryDrillDownContext;
  @Input() divisionId!: number;
  @Input() divisionName!: string;
  @Input() subDivisionId!: number;
  @Input() subDivisionName!: string;
  @Output() villageClosed = new EventEmitter<boolean>();
  @Output() toDashboardComp = new EventEmitter<boolean>();

  villages: LotteryDrillDownRow[] = [];
  filteredVillages: LotteryDrillDownRow[] = [];
  loading = true;

  beneficiaryView = false;
  selectedVillage!: { id: number; name: string };
  selectedVillageTotals?: { totalTarget: number; applicationsReceived: number; benefittedCount: number; balanceTarget: number };

  showOnlyWithTarget = true;
  private searchText = '';

  totalTarget = 0;
  totalApplied = 0;
  totalAssigned = 0;
  totalBalanceTarget = 0;

  private readonly excelColumns: string[] = [
    'Name', 'Total Target', 'Applications Received', 'Benefitted Count', 'Balance Target',
    'Application Percentage', 'Benefitted Percentage'
  ];

  constructor(
    private excelService: ExcelService,
    private lotteryService: LotteryService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.loading = true;
    this.lotteryService
      .getDrillDownList(
        this.drillData.financialYearId, this.drillData.departmentId, this.drillData.month, this.drillData.dbtGoId, this.drillData.dbtSchemeId,
        this.drillData.lotteryRunId, this.divisionId, this.subDivisionId, true)
      .subscribe((result: LotteryDrillDownRow[]) => {
        this.filteredVillages = result || [];
        this.totalTarget = this.sumBy('totalTarget');
        this.totalApplied = this.sumBy('applicationsReceived');
        this.totalAssigned = this.sumBy('benefittedCount');
        this.totalBalanceTarget = this.sumBy('balanceTarget');
        this.applyFilters();
        this.loading = false;
      }, () => {
        this.loading = false;
      });
  }

  get pageHeading(): string {
    if (!this.drillData) {
      return '';
    }
    const schemeComponent = [this.drillData.schemeName, this.drillData.productName, this.drillData.goName, this.drillData.componentName]
      .filter(v => !!v).join(' | ');
    return `${this.drillData.departmentName || ''} | ${this.drillData.financialYearName || ''} | ${schemeComponent}`;
  }

  private sumBy(key: keyof LotteryDrillDownRow): number {
    return this.filteredVillages.reduce((sum, v) => sum + Number(v[key]), 0);
  }

  private applyFilters(): void {
    let list = this.filteredVillages;
    if (this.showOnlyWithTarget) {
      list = list.filter(v => Number(v.totalTarget) > 0);
    }
    if (this.searchText) {
      list = list.filter(v => v.name.trim().toLowerCase().startsWith(this.searchText));
    }
    this.villages = list;
  }

  onTargetFilterChange(): void {
    this.applyFilters();
  }

  viewVillage(village: LotteryDrillDownRow): void {
    this.selectedVillage = { id: village.id, name: village.name };
    this.selectedVillageTotals = {
      totalTarget: village.totalTarget,
      applicationsReceived: village.applicationsReceived,
      benefittedCount: village.benefittedCount,
      balanceTarget: village.balanceTarget
    };
    this.beneficiaryView = true;
  }

  onBeneficiaryClosed(oneLevelBack: boolean): void {
    if (oneLevelBack) {
      this.beneficiaryView = false;
    } else {
      this.onCancel();
    }
  }

  onBeneficiaryClosedToDivision(): void {
    this.goToState();
  }

  onBeneficiaryToDashboard(): void {
    this.goToDashboard();
  }

  viewSubsidyPattern(village: LotteryDrillDownRow): void {
    this.openSubsidyPattern('Village', village.name, this.divisionId, this.subDivisionId, village.id);
  }

  onCancel(): void {
    this.villageClosed.emit(true);
  }

  viewOverallSubsidyPattern(): void {
    this.openSubsidyPattern('Sub-Division', this.subDivisionName, this.divisionId, this.subDivisionId, 0);
  }

  private openSubsidyPattern(levelLabel: string, name: string, divisionId: number, subDivisionId: number, villageId: number): void {
    this.lotteryService.getSubsidyPattern(
      this.drillData.financialYearId, this.drillData.departmentId, this.drillData.month,
      this.drillData.dbtGoId, this.drillData.dbtSchemeId, this.drillData.lotteryRunId,
      divisionId, subDivisionId, villageId, true
    ).subscribe((result: LotterySubsidyPattern) => {
      this.dialog.open(SubsidyPatternViewComponent, {
        autoFocus: false,
        width: '900px',
        panelClass: 'mat-dialog-serviceschedule',
        data: {
          levelLabel, name,
          financialYear: this.drillData.financialYearName,
          goid: this.drillData.goName,
          scheme: this.drillData.schemeName,
          component: this.drillData.componentName,
          product: this.drillData.productName,
          remainingTarget: result?.remainingTarget ?? 0,
          remainingSubsidyAmount: result?.remainingSubsidyAmount ?? 0,
          rows: result?.rows ?? []
        }
      });
    });
  }

  onSearchChange(event: any): void {
    this.searchText = event.target.value.trim().toLowerCase();
    this.applyFilters();
  }

  goToState(): void {
    this.villageClosed.emit(false);
  }

  goToDashboard(): void {
    this.toDashboardComp.emit(true);
  }

  exportAsXLSX(): void {
    const data = this.villages.map(v => ({
      Name: v.name,
      TotalTarget: v.totalTarget,
      ApplicationsReceived: v.applicationsReceived,
      BenefittedCount: v.benefittedCount,
      BalanceTarget: v.balanceTarget,
      ApplicationPercentage: v.applicationPercentage,
      BenefittedPercentage: v.benefittedPercentage
    }));
    this.excelService.exportAsExcelFile(data, 'Village Breakdown', this.excelColumns);
  }
}
